import type { QueryClient } from "@tanstack/react-query";
import { createRootRouteWithContext, createRoute, createRouter, Outlet, redirect } from "@tanstack/react-router";
import { meQuery } from "./auth.ts";
import { DeskPage } from "./desk/DeskPage.tsx";
import { deskQuery } from "./desk/desk.ts";
import { LoginPage } from "./login/LoginPage.tsx";
import { NotFound, ServerError } from "./ErrorPages.tsx";
import { ScanPage } from "./scan/ScanPage.tsx";
import { SectionPage } from "./SectionPage.tsx";
import { Shell } from "./shell/Shell.tsx";
import { UiKitPage } from "./ui-kit/UiKitPage.tsx";
import { IssuePage } from "./stock/IssuePage.tsx";
import { ProductPage } from "./stock/ProductPage.tsx";
import { RequestDraftPage } from "./stock/RequestDraftPage.tsx";
import { RequestsPage } from "./stock/RequestsPage.tsx";
import { ShelfPage, type ShelfMode } from "./stock/ShelfPage.tsx";
import { StockHub } from "./stock/StockHub.tsx";
import { TodayPage, type TodayTab } from "./stock/TodayPage.tsx";
import type { WarehouseCode } from "./stock/types.ts";
import { OrderPage } from "./purchasing/OrderPage.tsx";
import { LinePage } from "./production/LinePage.tsx";
import { PartnerPage, PartnersPage } from "./catalog/PartnersPage.tsx";
import { NewProductPage, ProductsPage } from "./catalog/ProductsPage.tsx";
import { TemplatePage } from "./catalog/TemplatePage.tsx";
import { MoPage } from "./production/MoPage.tsx";
import { MyOpsPage } from "./production/MyOpsPage.tsx";
import { UnitPage } from "./production/UnitPage.tsx";
import { NewProductionOrderPage, ProductionOrdersPage } from "./production/OrdersPage.tsx";
import { BomPage, BomsPage, NewBomPage } from "./production/BomPage.tsx";
import { PurchasingPage, type PurchasingTab } from "./purchasing/PurchasingPage.tsx";
import { AttendancePage, type AttendanceTab } from "./people/AttendancePage.tsx";
import { DoorsPage, type DoorsTab } from "./people/DoorsPage.tsx";
import { EmployeePage, EmployeesPage } from "./people/EmployeesPage.tsx";
import { ExpensesPage } from "./people/ExpensesPage.tsx";
import { HrHub } from "./people/HrHub.tsx";
import { KioskPage } from "./people/KioskPage.tsx";
import { LeavePage } from "./people/LeavePage.tsx";
import { TerminalsPage, type TerminalsTab } from "./people/TerminalsPage.tsx";
import { SalesOrderPage } from "./sales/SalesOrderPage.tsx";
import { NewSalesOrderPage, SalesPage } from "./sales/SalesPage.tsx";
import { listSearch } from "./docs/DocumentList.tsx";
import { RepairPage } from "./service/RepairPage.tsx";
import { NewRepairPage, QuarantinePage, RepairsPage, RepairWorksPage } from "./service/ServiceLists.tsx";
import { FirmwarePage, NewFirmwarePage, NewSkillPage, NewVersionPage, SkillsPage, TemplatesPage, VersionsPage } from "./catalog/DirectoryLists.tsx";
import { NewRouteCardPage, RouteCardPage, RouteCardsPage } from "./production/RouteCards.tsx";
import { MonthClosePage } from "./costing/MonthClosePage.tsx";
import { PnlPage } from "./costing/PnlPage.tsx";
import { PricesPage } from "./costing/PricesPage.tsx";
import { AccountPage } from "./money/AccountPage.tsx";
import { BankPage, type BankTab } from "./money/BankPage.tsx";
import { OpexPage, type OpexTab } from "./money/OpexPage.tsx";
import { AccountsListPage, EditAccountPage, NewAccountPage, PaymentPage, PaymentsListPage, StatementPage, StatementsListPage } from "./money/BankDocs.tsx";
import { ArticlesListPage, EditArticlePage, EditRulePage, EditTemplatePage, EntriesListPage, EntryPage, NewArticlePage, NewEntryPage, NewRulePage, NewTemplatePage, RulesListPage, TemplatesListPage } from "./money/OpexDocs.tsx";

interface Context {
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<Context>()({
  component: Outlet,
  notFoundComponent: NotFound,
});

export interface LoginSearch {
  error?: string;
  out?: 1;
}

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  validateSearch: (s: Record<string, unknown>): LoginSearch => ({
    error: typeof s.error === "string" ? s.error : undefined,
    out: s.out === 1 || s.out === "1" ? 1 : undefined,
  }),
  component: LoginPage,
});

/**
 * Киоск у входа (ATT-01): планшет без входа пользователя, со своим токеном
 * устройства. Вне оболочки и без проверки сессии.
 */
const kioskRoute = createRoute({ getParentRoute: () => rootRoute, path: "/kiosk", component: KioskPage });

/** Всё, что за входом: оболочка и экраны. */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "app",
  beforeLoad: async ({ context, location }) => {
    // Отказ входа сервер отдаёт на /?login_error=<код> (поток B, OIDC).
    const loginError = (location.search as Record<string, unknown>).login_error;
    if (typeof loginError === "string") throw redirect({ to: "/login", search: { error: loginError } });
    const me = await context.queryClient.query(meQuery);
    if (!me) throw redirect({ to: "/login", search: {} });
    return { me };
  },
  component: Shell,
  errorComponent: ServerError,
});

const deskRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/",
  loader: ({ context }) => context.queryClient.query(deskQuery),
  component: DeskPage,
});

const sectionRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/s/$sectionId",
  component: SectionPage,
});

const scanRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/scan/$code",
  component: ScanPage,
});

const uiKitRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/ui",
  component: UiKitPage,
});

const oneOf = <T extends string>(v: unknown, values: readonly T[]): T | undefined =>
  typeof v === "string" && (values as readonly string[]).includes(v) ? (v as T) : undefined;

const WAREHOUSE_CODES = ["MAIN", "SER", "BPK", "RND", "SRV", "FG"] as const;

const stockRoute = createRoute({ getParentRoute: () => appRoute, path: "/stock", component: StockHub });

const shelfRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/stock/shelf",
  validateSearch: (s: Record<string, unknown>): { code?: string; mode?: ShelfMode } => ({
    code: typeof s.code === "string" && s.code ? s.code : undefined,
    mode: oneOf(s.mode, ["receive", "issue", "count"] as const),
  }),
  component: ShelfPage,
});

const productRoute = createRoute({ getParentRoute: () => appRoute, path: "/stock/product/$sku", component: ProductPage });

const todayRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/stock/today",
  validateSearch: (s: Record<string, unknown>): { tab?: TodayTab } => ({ tab: oneOf(s.tab, ["receive", "issue", "count"] as const) }),
  component: TodayPage,
});

const issueRoute = createRoute({ getParentRoute: () => appRoute, path: "/stock/issue/$id", component: IssuePage });

const requestsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/stock/requests",
  validateSearch: (s: Record<string, unknown>): { warehouse?: WarehouseCode } => ({ warehouse: oneOf(s.warehouse, WAREHOUSE_CODES) }),
  component: RequestsPage,
});

const requestNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/stock/requests/new",
  validateSearch: (s: Record<string, unknown>): { warehouse?: WarehouseCode } => ({ warehouse: oneOf(s.warehouse, WAREHOUSE_CODES) }),
  component: RequestDraftPage,
});

const purchasingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/purchasing",
  validateSearch: (s: Record<string, unknown>): { tab?: PurchasingTab } => ({ tab: oneOf(s.tab, ["shortage", "expected", "drafts"] as const) }),
  component: PurchasingPage,
});

const orderRoute = createRoute({ getParentRoute: () => appRoute, path: "/purchasing/orders/$id", component: OrderPage });

const serialSearch = (s: Record<string, unknown>): { serial?: string } => ({
  serial: typeof s.serial === "string" && s.serial ? s.serial : undefined,
});

const lineRoute = createRoute({ getParentRoute: () => appRoute, path: "/production/line", component: LinePage });
const moRoute = createRoute({ getParentRoute: () => appRoute, path: "/production/orders/$id", component: MoPage });
const moListRoute = createRoute({ getParentRoute: () => appRoute, path: "/production/orders", validateSearch: listSearch, component: ProductionOrdersPage });
const moNewRoute = createRoute({ getParentRoute: () => appRoute, path: "/production/orders/new", component: NewProductionOrderPage });
const bomsRoute = createRoute({ getParentRoute: () => appRoute, path: "/production/boms", validateSearch: listSearch, component: BomsPage });
const bomNewRoute = createRoute({ getParentRoute: () => appRoute, path: "/production/boms/new", component: NewBomPage });
const bomRoute = createRoute({ getParentRoute: () => appRoute, path: "/production/boms/$productId", component: BomPage });
const myOpsRoute = createRoute({ getParentRoute: () => appRoute, path: "/production/my", validateSearch: serialSearch, component: MyOpsPage });
const unitRoute = createRoute({ getParentRoute: () => appRoute, path: "/production/unit", validateSearch: serialSearch, component: UnitPage });

const productsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/catalog/products",
  validateSearch: (s: Record<string, unknown>): { q?: string } => ({ q: typeof s.q === "string" && s.q ? s.q : undefined }),
  component: ProductsPage,
});
const newProductRoute = createRoute({ getParentRoute: () => appRoute, path: "/catalog/products/new", component: NewProductPage });
const templateRoute = createRoute({ getParentRoute: () => appRoute, path: "/catalog/templates/$id", component: TemplatePage });
const partnersRoute = createRoute({ getParentRoute: () => appRoute, path: "/partners", component: PartnersPage });
const partnerRoute = createRoute({ getParentRoute: () => appRoute, path: "/partners/$id", component: PartnerPage });

const isoDay = (v: unknown): string | undefined => (typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : undefined);
const isoMonth = (v: unknown): string | undefined => (typeof v === "string" && /^\d{4}-\d{2}$/.test(v) ? v : undefined);

const hrRoute = createRoute({ getParentRoute: () => appRoute, path: "/hr", component: HrHub });
const employeesRoute = createRoute({ getParentRoute: () => appRoute, path: "/hr/employees", component: EmployeesPage });
const employeeRoute = createRoute({ getParentRoute: () => appRoute, path: "/hr/employees/$id", component: EmployeePage });
const attendanceRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/attendance",
  validateSearch: (s: Record<string, unknown>): { tab?: AttendanceTab; date?: string; month?: string } => ({
    tab: oneOf(s.tab, ["marks", "timesheet", "settings"] as const),
    date: isoDay(s.date),
    month: isoMonth(s.month),
  }),
  component: AttendancePage,
});
const leaveRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hr/leave",
  validateSearch: (s: Record<string, unknown>): { employee?: string } => ({ employee: typeof s.employee === "string" && s.employee ? s.employee : undefined }),
  component: LeavePage,
});
const expensesRoute = createRoute({ getParentRoute: () => appRoute, path: "/hr/expenses", component: ExpensesPage });
const doorsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hr/doors",
  validateSearch: (s: Record<string, unknown>): { tab?: DoorsTab; date?: string } => ({ tab: oneOf(s.tab, ["day", "log", "panels"] as const), date: isoDay(s.date) }),
  component: DoorsPage,
});
const terminalsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hr/terminals",
  validateSearch: (s: Record<string, unknown>): { tab?: TerminalsTab; date?: string } => ({ tab: oneOf(s.tab, ["devices", "sessions"] as const), date: isoDay(s.date) }),
  component: TerminalsPage,
});
// /sales из прежних ссылок и плиток ведёт на список заказов (схема /<раздел>/<документ>).
const salesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/sales",
  beforeLoad: () => {
    throw redirect({ to: "/sales/orders", search: {} });
  },
});
const salesOrdersRoute = createRoute({ getParentRoute: () => appRoute, path: "/sales/orders", validateSearch: listSearch, component: SalesPage });
const newSalesOrderRoute = createRoute({ getParentRoute: () => appRoute, path: "/sales/orders/new", component: NewSalesOrderPage });
const salesOrderRoute = createRoute({ getParentRoute: () => appRoute, path: "/sales/orders/$id", component: SalesOrderPage });
const repairRoute = createRoute({ getParentRoute: () => appRoute, path: "/service/repairs/$id", component: RepairPage });
// «Операції» ремонта, справочника и карт маршрута: список, форма new раньше $id.
const repairsRoute = createRoute({ getParentRoute: () => appRoute, path: "/service/repairs", validateSearch: listSearch, component: RepairsPage });
const newRepairRoute = createRoute({ getParentRoute: () => appRoute, path: "/service/repairs/new", validateSearch: serialSearch, component: NewRepairPage });
const repairWorksRoute = createRoute({ getParentRoute: () => appRoute, path: "/service/works", validateSearch: listSearch, component: RepairWorksPage });
const quarantineRoute = createRoute({ getParentRoute: () => appRoute, path: "/service/quarantine", validateSearch: listSearch, component: QuarantinePage });
const templatesRoute = createRoute({ getParentRoute: () => appRoute, path: "/catalog/templates", validateSearch: listSearch, component: TemplatesPage });
const versionsRoute = createRoute({ getParentRoute: () => appRoute, path: "/catalog/versions", validateSearch: listSearch, component: VersionsPage });
const newVersionRoute = createRoute({ getParentRoute: () => appRoute, path: "/catalog/versions/new", component: NewVersionPage });
const firmwareRoute = createRoute({ getParentRoute: () => appRoute, path: "/catalog/firmware", validateSearch: listSearch, component: FirmwarePage });
const newFirmwareRoute = createRoute({ getParentRoute: () => appRoute, path: "/catalog/firmware/new", component: NewFirmwarePage });
const skillsRoute = createRoute({ getParentRoute: () => appRoute, path: "/catalog/skills", validateSearch: listSearch, component: SkillsPage });
const newSkillRoute = createRoute({ getParentRoute: () => appRoute, path: "/catalog/skills/new", component: NewSkillPage });
const routeCardsRoute = createRoute({ getParentRoute: () => appRoute, path: "/production/cards", validateSearch: listSearch, component: RouteCardsPage });
const newRouteCardRoute = createRoute({ getParentRoute: () => appRoute, path: "/production/cards/new", component: NewRouteCardPage });
const routeCardRoute = createRoute({ getParentRoute: () => appRoute, path: "/production/cards/$id", component: RouteCardPage });

const str = (v: unknown) => (typeof v === "string" && v ? v : undefined);
const monthSearch = (s: Record<string, unknown>): { month?: string } => ({ month: str(s.month) });
const closeRoute = createRoute({ getParentRoute: () => appRoute, path: "/costing/close", validateSearch: monthSearch, component: MonthClosePage });
const pnlRoute = createRoute({ getParentRoute: () => appRoute, path: "/costing/pnl", validateSearch: monthSearch, component: PnlPage });
const pricesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/costing/prices",
  validateSearch: (s: Record<string, unknown>): { product?: string; serial?: string } => ({ product: str(s.product), serial: str(s.serial) }),
  component: PricesPage,
});
const bankRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/money/bank",
  validateSearch: (s: Record<string, unknown>): { tab?: BankTab } => ({ tab: oneOf(s.tab, ["accounts", "pending"] as const) }),
  component: BankPage,
});
const accountRoute = createRoute({ getParentRoute: () => appRoute, path: "/money/bank/accounts/$id", component: AccountPage });
// «Операції» банка и «Витрат»: списки, формы (статический new выигрывает у $id) и карточки.
const moneyBankAccountsRoute = createRoute({ getParentRoute: () => appRoute, path: "/money/bank/accounts", validateSearch: listSearch, component: AccountsListPage });
const moneyBankAccountsNewRoute = createRoute({ getParentRoute: () => appRoute, path: "/money/bank/accounts/new", component: NewAccountPage });
const moneyBankAccountsIdEditRoute = createRoute({ getParentRoute: () => appRoute, path: "/money/bank/accounts/$id/edit", component: EditAccountPage });
const moneyBankPaymentsRoute = createRoute({ getParentRoute: () => appRoute, path: "/money/bank/payments", validateSearch: listSearch, component: PaymentsListPage });
const moneyBankPaymentsIdRoute = createRoute({ getParentRoute: () => appRoute, path: "/money/bank/payments/$id", component: PaymentPage });
const moneyBankStatementsRoute = createRoute({ getParentRoute: () => appRoute, path: "/money/bank/statements", validateSearch: listSearch, component: StatementsListPage });
const moneyBankStatementsIdRoute = createRoute({ getParentRoute: () => appRoute, path: "/money/bank/statements/$id", component: StatementPage });
const moneyOpexEntriesRoute = createRoute({ getParentRoute: () => appRoute, path: "/money/opex/entries", validateSearch: listSearch, component: EntriesListPage });
const moneyOpexEntriesNewRoute = createRoute({ getParentRoute: () => appRoute, path: "/money/opex/entries/new", component: NewEntryPage });
const moneyOpexEntriesIdRoute = createRoute({ getParentRoute: () => appRoute, path: "/money/opex/entries/$id", component: EntryPage });
const moneyOpexArticlesRoute = createRoute({ getParentRoute: () => appRoute, path: "/money/opex/articles", validateSearch: listSearch, component: ArticlesListPage });
const moneyOpexArticlesNewRoute = createRoute({ getParentRoute: () => appRoute, path: "/money/opex/articles/new", component: NewArticlePage });
const moneyOpexArticlesCodeRoute = createRoute({ getParentRoute: () => appRoute, path: "/money/opex/articles/$code", component: EditArticlePage });
const moneyOpexRulesRoute = createRoute({ getParentRoute: () => appRoute, path: "/money/opex/rules", validateSearch: listSearch, component: RulesListPage });
const moneyOpexRulesNewRoute = createRoute({ getParentRoute: () => appRoute, path: "/money/opex/rules/new", component: NewRulePage });
const moneyOpexRulesIdRoute = createRoute({ getParentRoute: () => appRoute, path: "/money/opex/rules/$id", component: EditRulePage });
const moneyOpexTemplatesRoute = createRoute({ getParentRoute: () => appRoute, path: "/money/opex/templates", validateSearch: listSearch, component: TemplatesListPage });
const moneyOpexTemplatesNewRoute = createRoute({ getParentRoute: () => appRoute, path: "/money/opex/templates/new", component: NewTemplatePage });
const moneyOpexTemplatesIdRoute = createRoute({ getParentRoute: () => appRoute, path: "/money/opex/templates/$id", component: EditTemplatePage });
const opexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/money/opex",
  validateSearch: (s: Record<string, unknown>): { tab?: OpexTab } => ({ tab: oneOf(s.tab, ["entries", "rules", "templates"] as const) }),
  component: OpexPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  kioskRoute,
  appRoute.addChildren([
    deskRoute,
    sectionRoute,
    scanRoute,
    uiKitRoute,
    stockRoute,
    shelfRoute,
    productRoute,
    todayRoute,
    issueRoute,
    requestsRoute,
    requestNewRoute,
    purchasingRoute,
    orderRoute,
    lineRoute,
    moRoute,
    moListRoute,
    moNewRoute,
    bomsRoute,
    bomNewRoute,
    bomRoute,
    myOpsRoute,
    unitRoute,
    productsRoute,
    newProductRoute,
    templateRoute,
    partnersRoute,
    partnerRoute,
    hrRoute,
    employeesRoute,
    employeeRoute,
    attendanceRoute,
    leaveRoute,
    expensesRoute,
    doorsRoute,
    terminalsRoute,
    salesRoute,
    salesOrdersRoute,
    newSalesOrderRoute,
    salesOrderRoute,
    repairRoute,
    repairsRoute,
    newRepairRoute,
    repairWorksRoute,
    quarantineRoute,
    templatesRoute,
    versionsRoute,
    newVersionRoute,
    firmwareRoute,
    newFirmwareRoute,
    skillsRoute,
    newSkillRoute,
    routeCardsRoute,
    newRouteCardRoute,
    routeCardRoute,
    closeRoute,
    pnlRoute,
    pricesRoute,
    bankRoute,
    accountRoute,
    opexRoute,
    moneyBankAccountsRoute,
    moneyBankAccountsNewRoute,
    moneyBankAccountsIdEditRoute,
    moneyBankPaymentsRoute,
    moneyBankPaymentsIdRoute,
    moneyBankStatementsRoute,
    moneyBankStatementsIdRoute,
    moneyOpexEntriesRoute,
    moneyOpexEntriesNewRoute,
    moneyOpexEntriesIdRoute,
    moneyOpexArticlesRoute,
    moneyOpexArticlesNewRoute,
    moneyOpexArticlesCodeRoute,
    moneyOpexRulesRoute,
    moneyOpexRulesNewRoute,
    moneyOpexRulesIdRoute,
    moneyOpexTemplatesRoute,
    moneyOpexTemplatesNewRoute,
    moneyOpexTemplatesIdRoute,
  ]),
]);

export function makeRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: "intent",
    // Данные держит Query, у роутера свой кэш не нужен.
    defaultPreloadStaleTime: 0,
    scrollRestoration: true,
  });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof makeRouter>;
  }
}
